/**
 * QuestionEditorModal — add/edit one question in a modal (WCR parity). New
 * questions start on the type-picker grid, then the details form; editing
 * opens straight to details with a "Change question type" affordance.
 *
 * Two SiteOps invariants the WCR admin violated are upheld here:
 *   - question.key is minted ONCE (`form.q-*`) and never changes — it is the
 *     join key into form_answers / contact_answer_facts. Imported questions
 *     keep their original id.
 *   - option.value is STABLE: a label edit never rewrites the stored value
 *     (the old WCR editor derived value from label on every keystroke and
 *     silently broke show_if/eligibility rules — see crmForms.js note). The UI
 *     shows only the label; values are minted once as option_N.
 */
import { useState } from 'react';
import { FormQuestionDef, FormShowIf, FormEligibilityRule, FormMedicalFact } from '../../types';
import Modal from './Modal';
import QuestionTypeSelector from './QuestionTypeSelector';
import ConditionBuilder from './ConditionBuilder';
import EligibilityRuleBuilder from './EligibilityRuleBuilder';
import CalculationEditor from './CalculationEditor';
import ProfileKeyPicker, { suggestValueTypeFor } from './ProfileKeyPicker';
import { CALC_PRESET_KEYS, eligibilityOperatorsForType, numberInputProps } from './formMeta';
import { Alert } from '../ui';

const mintKey = () => `form.q-${Math.random().toString(36).slice(2, 10)}`;

const mintOptionValue = (options: { value: string }[]) => {
  const used = new Set(options.map((o) => o.value));
  for (let n = options.length + 1; ; n++) {
    const candidate = `option_${n}`;
    if (!used.has(candidate)) return candidate;
  }
};

const PLACEHOLDER_TYPES = ['text', 'textarea', 'email', 'phone', 'number'];

const inputCls =
  'block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';
const labelCls = 'mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300';

interface QuestionEditorModalProps {
  isOpen: boolean;
  /** null = adding a new question. */
  question: FormQuestionDef | null;
  /** Every question in the form (for branching + calculation cross-refs). */
  allQuestions: FormQuestionDef[];
  onSave: (q: FormQuestionDef) => void;
  onClose: () => void;
}

function EditorBody({ question, allQuestions, onSave, onClose }: Omit<QuestionEditorModalProps, 'isOpen'>) {
  const editing = !!question;
  const [step, setStep] = useState<'type' | 'details'>(editing ? 'details' : 'type');
  const [type, setType] = useState<string>(question?.type || 'text');
  const [label, setLabel] = useState(question?.label || '');
  const [description, setDescription] = useState(question?.description || '');
  const [placeholder, setPlaceholder] = useState(question?.placeholder || '');
  const [required, setRequired] = useState(question?.required !== false);
  const [options, setOptions] = useState<{ value: string; label: string }[]>(question?.options || []);
  const [min, setMin] = useState<number | undefined>(question?.min ?? undefined);
  const [max, setMax] = useState<number | undefined>(question?.max ?? undefined);
  const [showIf, setShowIf] = useState<FormShowIf | null>(question?.show_if || null);
  const [eligibility, setEligibility] = useState<FormEligibilityRule | null>(question?.eligibility || null);
  const [calculation, setCalculation] = useState<{ preset: string; inputs: Record<string, string> } | null>(question?.calculation || null);
  const [mfKind, setMfKind] = useState<string>(question?.medical_fact?.kind || '');
  const [mfName, setMfName] = useState(question?.medical_fact?.name || '');
  const [mfKey, setMfKey] = useState(question?.medical_fact?.profile_key || '');
  const [error, setError] = useState<string | null>(null);

  const isCalc = type === 'calculation';
  const needsOptions = type === 'single_choice' || type === 'multiple_choice';
  const needsMinMax = type === 'number' || type === 'scale';

  // Branching + calculation cross-refs may target any other question, but never
  // a calculation (resolves only at completion → dead branch the server rejects).
  const otherQuestions = allQuestions.filter((q) => q.key !== question?.key);
  const branchSources = otherQuestions.filter((q) => q.type !== 'calculation');
  const calcSources = otherQuestions.filter((q) => q.type !== 'calculation');

  const applyType = (next: string) => {
    setType(next);
    setStep('details');
    setError(null);
    if (next === 'single_choice' || next === 'multiple_choice') {
      setOptions((prev) => (prev.length > 0 ? prev : [
        { value: 'option_1', label: 'Option 1' },
        { value: 'option_2', label: 'Option 2' },
      ]));
    } else {
      setOptions([]);
    }
    if (next === 'scale') {
      // New builder scales are 0–10 (imported WCR scales materialize 1–10).
      setMin((m) => m ?? 0);
      setMax((m) => m ?? 10);
    } else if (next !== 'number') {
      setMin(undefined);
      setMax(undefined);
    }
    if (next === 'calculation') {
      setCalculation((c) => c || { preset: CALC_PRESET_KEYS[0], inputs: {} });
      setRequired(false);
      setShowIf(null);
    } else {
      setCalculation(null);
    }
    // Realign eligibility to the new type: an operator the new type no longer
    // offers would render a blank menu and a confusing publish error, and an
    // option-derived value (option_N) is meaningless once the option-ness
    // changes. `type` here is still the OLD type (setType is async). show_if is
    // NOT realigned — it references OTHER questions' types, not this one.
    const nextOps = eligibilityOperatorsForType(next);
    const wasOption = type === 'single_choice' || type === 'multiple_choice';
    const nowOption = next === 'single_choice' || next === 'multiple_choice';
    setEligibility((e) => {
      if (!e) return e;
      const fix = (r: { operator: string; value: any } | null | undefined) => {
        if (!r) return null;
        if (nextOps.includes(r.operator) && wasOption === nowOption) return r;
        const op = nextOps.includes(r.operator) ? r.operator : nextOps[0];
        const value = op === 'between' || op === 'not_between' ? ['', ''] : op === 'in' ? [] : '';
        return { operator: op, value };
      };
      const ex = fix(e.exclude_if);
      const fl = fix(e.flag_for_review_if);
      return ex || fl ? { exclude_if: ex, flag_for_review_if: fl } : null;
    });
  };

  const updateOptionLabel = (i: number, value: string) => {
    setOptions((prev) => prev.map((o, j) => (j === i ? { ...o, label: value } : o)));
  };
  const addOption = () => setOptions((prev) => [...prev, { value: mintOptionValue(prev), label: `Option ${prev.length + 1}` }]);
  const removeOption = (i: number) => setOptions((prev) => prev.filter((_, j) => j !== i));

  const save = () => {
    const trimmed = label.trim();
    if (!trimmed) { setError(isCalc ? 'Give this calculation an internal label.' : 'Enter the question text.'); return; }
    if (needsOptions && options.filter((o) => o.label.trim()).length < 2) {
      setError('Add at least 2 options.'); return;
    }
    if (needsMinMax && min != null && max != null && min > max) {
      setError('Minimum must not exceed maximum.'); return;
    }
    const out: FormQuestionDef = {
      key: question?.key || mintKey(),
      type,
      label: trimmed,
      required: isCalc ? false : required,
    };
    if (description.trim()) out.description = description.trim();
    if (!isCalc && placeholder.trim() && PLACEHOLDER_TYPES.includes(type)) out.placeholder = placeholder.trim();
    if (needsOptions) out.options = options.filter((o) => o.label.trim());
    if (needsMinMax && min != null) out.min = min;
    if (needsMinMax && max != null) out.max = max;
    if (!isCalc && showIf) out.show_if = showIf;
    if (eligibility && (eligibility.exclude_if || eligibility.flag_for_review_if)) out.eligibility = eligibility;
    if (isCalc && calculation) out.calculation = calculation;
    if (mfKind) {
      if (!mfName.trim() && !mfKey.trim()) {
        setError('Enter a fact name (or a profile key) to record this on the medical profile.'); return;
      }
      const mf: FormMedicalFact = { kind: mfKind as FormMedicalFact['kind'] };
      if (mfName.trim()) mf.name = mfName.trim();
      if (mfKey.trim()) mf.profile_key = mfKey.trim().toLowerCase();
      // Preserve a positive_when authored elsewhere (no editor for it yet).
      if (question?.medical_fact?.positive_when) mf.positive_when = question.medical_fact.positive_when;
      out.medical_fact = mf;
    }
    onSave(out);
  };

  // Step 1: type picker (new questions only).
  if (step === 'type') {
    return (
      <div className="space-y-5">
        <p className="text-sm text-gray-500 dark:text-gray-400">Choose the type of question you want to add.</p>
        <QuestionTypeSelector onSelect={applyType} />
        <div className="flex justify-end">
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <button type="button" onClick={() => setStep('type')} className="text-sm font-medium text-primary-700 hover:underline dark:text-primary-400">
        ← Change question type
      </button>

      {isCalc ? (
        <>
          <CalculationEditor
            config={calculation}
            questions={calcSources}
            onChange={(c) => setCalculation(c)}
          />
          <div>
            <label className={labelCls}>Label</label>
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Calculation label" className={inputCls} />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Internal label — recruiters see it on responses; patients never see calculations.</p>
          </div>
        </>
      ) : (
        <>
          <div>
            <label className={labelCls}>Question <span className="text-red-500">*</span></label>
            <textarea rows={2} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Enter your question" className={inputCls} />
          </div>

          <div>
            <label className={labelCls}>Help Text (optional)</label>
            <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Additional context or instructions" className={inputCls} />
          </div>

          {PLACEHOLDER_TYPES.includes(type) && (
            <div>
              <label className={labelCls}>Placeholder (optional)</label>
              <input value={placeholder} onChange={(e) => setPlaceholder(e.target.value)} placeholder="Placeholder text" className={inputCls} />
            </div>
          )}

          {needsOptions && (
            <div>
              <label className={labelCls}>Options <span className="text-red-500">*</span></label>
              <div className="space-y-2">
                {options.map((o, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input value={o.label} onChange={(e) => updateOptionLabel(i, e.target.value)} placeholder="Option label" className={`${inputCls} flex-1`} />
                    <button type="button" onClick={() => removeOption(i)} className="shrink-0 rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20" aria-label="Remove option">
                      ✕
                    </button>
                  </div>
                ))}
                <button type="button" onClick={addOption} className="text-sm font-medium text-primary-700 hover:underline dark:text-primary-400">
                  + Add Option
                </button>
              </div>
            </div>
          )}

          {needsMinMax && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Minimum</label>
                <input type="number" {...numberInputProps} value={min ?? ''} onChange={(e) => setMin(e.target.value === '' ? undefined : Number(e.target.value))} placeholder="Min value" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Maximum</label>
                <input type="number" {...numberInputProps} value={max ?? ''} onChange={(e) => setMax(e.target.value === '' ? undefined : Number(e.target.value))} placeholder="Max value" className={inputCls} />
              </div>
            </div>
          )}

          <label className="flex items-center gap-2.5 text-sm text-gray-700 dark:text-gray-200">
            <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700" />
            Required question
          </label>

          <div className="border-t border-gray-200 pt-5 dark:border-gray-700">
            <h4 className="mb-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100">Conditional Display (optional)</h4>
            <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">Only show this question when a previous answer meets certain criteria.</p>
            <ConditionBuilder condition={showIf} questions={branchSources} onChange={setShowIf} />
          </div>
        </>
      )}

      <div className="border-t border-gray-200 pt-5 dark:border-gray-700">
        <h4 className="mb-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100">Eligibility Rules (optional)</h4>
        <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
          Automatically determine eligibility based on this {isCalc ? 'calculated value' : 'answer'}.
        </p>
        <EligibilityRuleBuilder rule={eligibility} questionType={type} options={options} onChange={setEligibility} />
      </div>

      <div className="border-t border-gray-200 pt-5 dark:border-gray-700">
        <h4 className="mb-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100">Record on medical profile (optional)</h4>
        <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
          Save the answer as a structured fact on the patient&rsquo;s profile, so cohort queries (e.g. &ldquo;type 2 diabetes&rdquo;, &ldquo;BMI &gt; 30&rdquo;) capture this lead.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label className={labelCls}>Record as</label>
            <select value={mfKind} onChange={(e) => setMfKind(e.target.value)} className={inputCls}>
              <option value="">Don&rsquo;t record</option>
              <option value="condition">Condition / diagnosis</option>
              <option value="medication">Medication</option>
              <option value="lab">Lab / vital value</option>
            </select>
          </div>
          {mfKind && (
            <>
              <div>
                <label className={labelCls}>{mfKind === 'lab' ? 'Test name' : 'Fact name'}</label>
                <input value={mfName} onChange={(e) => setMfName(e.target.value)} placeholder={mfKind === 'lab' ? 'e.g. A1c' : 'e.g. Type 2 diabetes'} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Field tag (optional)</label>
                <ProfileKeyPicker
                  value={mfKey}
                  onChange={(k) => setMfKey(k || '')}
                  suggestValueType={suggestValueTypeFor(type)}
                  suggestLabel={mfName.trim() || label.trim()}
                  inputClassName={inputCls}
                />
              </div>
            </>
          )}
        </div>
        {mfKind && (
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {mfKind === 'lab'
              ? 'The numeric answer is stored as this test’s value. '
              : `An affirmative answer records “${mfName.trim() || 'the fact'}”; a "No" or blank answer is ignored. `}
            A shared field tag (profile key) enables precise cross-study querying — the library lives under Settings → Field tags.
          </p>
        )}
      </div>

      {error && (
        <Alert tone="error" icon={false}>{error}</Alert>
      )}

      <div className="flex justify-end gap-3 border-t border-gray-200 pt-5 dark:border-gray-700">
        <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
          Cancel
        </button>
        <button type="button" onClick={save} className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800">
          {editing ? 'Update Question' : 'Add Question'}
        </button>
      </div>
    </div>
  );
}

export default function QuestionEditorModal({ isOpen, question, allQuestions, onSave, onClose }: QuestionEditorModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={question ? 'Edit Question' : 'Add Question'}>
      {/* Mounted only while open (Modal returns null when closed), so state
          initializes fresh from `question` each time the editor opens. */}
      <EditorBody
        key={question?.key ?? 'new'}
        question={question}
        allQuestions={allQuestions}
        onSave={onSave}
        onClose={onClose}
      />
    </Modal>
  );
}
